import { z } from 'zod';
import { MobIdSchema } from './mobs.js';
/**
 * Biomes of the open world (ADR 0011). Data, not code: what the ground is
 * made of, which creatures live there, how its villages are built. The
 * generator (`layouts/world*.ts`) decides WHERE each biome lies; this table
 * decides WHAT it is.
 *
 * The valley is laid out like a compass around the capital: meadow in the
 * middle, forest to the west, swamp to the east, snow to the north, desert
 * to the south. Monsters grow stronger with the distance from the capital,
 * so the meadow is for newcomers and the far biomes for the seasoned.
 */
export const BIOME_IDS = ['meadow', 'forest', 'swamp', 'desert', 'snow'];
export const BiomeIdSchema = z.enum(BIOME_IDS);
export const BiomeDefinitionSchema = z.object({
    id: z.enum(BIOME_IDS),
    /** French, shown on the minimap and when a player crosses into it. */
    name: z.string().min(1),
    /**
     * The levels it is meant for. Its creatures stand inside this band and its
     * loot is the equipment whose required level falls in it (`data/loot.ts`):
     * the bands follow one another from 1 to the last creature's level.
     */
    level: z
        .object({ min: z.number().int().min(1), max: z.number().int().min(1) })
        .refine((band) => band.max >= band.min, 'max must be at least min'),
    /** The walkable ground everywhere nothing else grows: a terrain character. */
    ground: z.string().length(1),
    /**
     * Dens to scatter: which mob, how many dens, and the usual size of a pack.
     * Order matters: the first two also hold the summit caves, the last keeps
     * the biome's camps (`world-camps.ts`), so the one that pitches tents
     * (goblin, lizardman, mummy) comes last, not the beast.
     * Counted for the 384-tile world (ADR 0028): about one pack per 1 200
     * tiles of biome.
     * Each den draws its own size, from a pair to one more than `count`, and
     * `loners` more of the creature roam alone, to be fought one on one.
     */
    dens: z.array(z.object({
        mob: MobIdSchema,
        dens: z.number().int().min(0),
        count: z.number().int().min(1),
        loners: z.number().int().min(0).default(10),
    })),
    /**
     * Its elites (ADR 0023): one lair each, deep in the biome, holding a single
     * creature that comes back only long after it falls.
     */
    elites: z.object({ rare: MobIdSchema, epic: MobIdSchema, legendary: MobIdSchema }),
    /** French name of the biome's village; the meadow's is the capital. */
    village: z.string().min(1),
    /** Houses of its villages, prop ids; a village mixes them. */
    houses: z.array(z.string().min(1)).min(1),
    /**
     * What makes it a village and not a housing estate, prop ids by
     * importance: the capital builds them all, a hamlet the first two. They
     * take the ring nearest the square.
     */
    buildings: z.array(z.string().min(1)).default([]),
    /** Regional facades for the services present in every village. */
    services: z.object({
        forge: z.string().min(1),
        foundry: z.string().min(1),
        alchemy: z.string().min(1),
        shop: z.string().min(1),
        auction: z.string().min(1),
    }),
    /**
     * The townsperson who hands out the region's quests (ADR 0047), standing
     * on its village's square; none yet where no chain is written.
     */
    questGiver: MobIdSchema.optional(),
    /**
     * The mounts' keeper out of this biome's village, on the road to another
     * village (ADR 0047): the capital's stable on the way to Sylvebois, horses
     * in a paddock; Sablerouge's camel drivers' relay on the way to the
     * capital, camels roaming free around it.
     */
    stable: z
        .object({
        /** The building's prop id. */
        prop: z.string().min(1),
        /** The biome whose village the road leads to. */
        toward: z.enum(BIOME_IDS),
        /** French, shown on the maps. */
        name: z.string().min(1),
        /** Who stands at its door and hands a mount over. */
        keeper: MobIdSchema,
        /** The beasts about it. */
        herd: MobIdSchema,
        /** A fenced paddock beside it; free beasts roam a wider round otherwise. */
        fenced: z.boolean().default(true),
    })
        .optional(),
    /** The barn its farms share, if its farmers keep one. */
    barn: z.string().min(1).optional(),
    /** Minimap tint when a player hovers the legend; also the review sheets. */
    colour: z.string().regex(/^#[0-9a-f]{6}$/),
});
const DEFINITIONS = [
    {
        id: 'meadow',
        level: { min: 1, max: 4 },
        village: 'Havrebourg',
        name: 'Prairie',
        ground: '.',
        dens: [
            { mob: 'rat', dens: 10, count: 3, loners: 8 },
            // Foxes roam alone or in twos and threes, never in a crowd.
            { mob: 'fox', dens: 5, count: 2, loners: 8 },
            { mob: 'boar', dens: 7, count: 2, loners: 8 },
        ],
        elites: { rare: 'plague-rat', epic: 'scarred-boar', legendary: 'iron-snout' },
        houses: ['house-red', 'house-blue'],
        buildings: ['tavern', 'chapel', 'forge'],
        services: {
            forge: 'forge',
            foundry: 'foundry',
            alchemy: 'alchemy',
            shop: 'shop',
            auction: 'auction-meadow',
        },
        barn: 'barn',
        stable: {
            prop: 'stable',
            toward: 'forest',
            name: 'Écuries de Havrebourg',
            keeper: 'stablemaster',
            herd: 'horse',
        },
        questGiver: 'captain',
        colour: '#6aa84f',
    },
    {
        id: 'forest',
        level: { min: 5, max: 7 },
        village: 'Sylvebois',
        name: 'Forêt des Murmures',
        ground: '.',
        dens: [
            { mob: 'wolf', dens: 7, count: 3, loners: 8 },
            // Treants stand mostly alone among the trees, a fight one on one.
            { mob: 'treant', dens: 3, count: 2, loners: 12 },
            // Archers keep to small groups, behind the others' backs (ADR 0055).
            { mob: 'goblin-archer', dens: 4, count: 2, loners: 6 },
            { mob: 'goblin', dens: 7, count: 3, loners: 8 },
        ],
        elites: { rare: 'ash-wolf', epic: 'goblin-chief', legendary: 'silver-fang' },
        houses: ['house-forest-blue', 'house-forest-red'],
        buildings: ['tavern-forest', 'forge-forest'],
        services: {
            forge: 'forge-forest',
            foundry: 'foundry-forest',
            alchemy: 'alchemy-forest',
            shop: 'shop-forest',
            auction: 'auction-forest',
        },
        barn: 'barn',
        questGiver: 'ranger',
        colour: '#2f5d2a',
    },
    {
        id: 'swamp',
        level: { min: 8, max: 11 },
        village: 'Brumevase',
        name: 'Marais Brumeux',
        ground: 'h',
        dens: [
            { mob: 'toad', dens: 7, count: 2, loners: 8 },
            // Caimans lie in ambush alone or in small groups.
            { mob: 'caiman', dens: 4, count: 2, loners: 10 },
            { mob: 'wisp', dens: 3, count: 2, loners: 4 },
            { mob: 'lizardman', dens: 7, count: 2, loners: 8 },
        ],
        elites: { rare: 'venom-toad', epic: 'lizard-shaman', legendary: 'bog-king' },
        houses: ['house-swamp'],
        // The forge second: a hamlet builds the first two, and every village has its smith.
        buildings: ['tavern-swamp', 'forge-swamp', 'chapel'],
        services: {
            forge: 'forge-swamp',
            foundry: 'foundry-swamp',
            alchemy: 'alchemy-swamp',
            shop: 'shop-swamp',
            auction: 'auction-swamp',
        },
        barn: 'barn',
        questGiver: 'swamp-elder',
        colour: '#556b3a',
    },
    {
        id: 'desert',
        level: { min: 12, max: 15 },
        village: 'Sablerouge',
        name: 'Désert de Braise',
        ground: 's',
        dens: [
            { mob: 'hyena', dens: 6, count: 3, loners: 6 },
            { mob: 'scorpion', dens: 4, count: 2, loners: 8 },
            { mob: 'ember-salamander', dens: 4, count: 2, loners: 6 },
            { mob: 'cobra', dens: 4, count: 2, loners: 6 },
            { mob: 'mummy', dens: 6, count: 2, loners: 6 },
        ],
        elites: { rare: 'mangy-hyena', epic: 'cursed-mummy', legendary: 'forgotten-pharaoh' },
        houses: ['adobe'],
        buildings: ['caravanserai', 'forge-desert'],
        stable: {
            prop: 'camel-tent',
            toward: 'meadow',
            name: 'Relais des chameliers',
            keeper: 'camel-driver',
            herd: 'camel',
            fenced: false,
        },
        services: {
            forge: 'forge-desert',
            foundry: 'foundry-desert',
            alchemy: 'alchemy-desert',
            shop: 'shop-desert',
            auction: 'auction-desert',
        },
        questGiver: 'caravan-master',
        colour: '#d9b86a',
    },
    {
        id: 'snow',
        level: { min: 16, max: 20 },
        village: 'Givrecime',
        name: 'Pics Gelés',
        ground: 'n',
        dens: [
            // Big packs: the snow's pack hunters come five at a time.
            { mob: 'ice-wolf', dens: 5, count: 4, loners: 4 },
            { mob: 'polar-bear', dens: 6, count: 2, loners: 6 },
            { mob: 'frost-revenant', dens: 5, count: 2, loners: 6 },
            { mob: 'snow-harpy', dens: 4, count: 2, loners: 6 },
            { mob: 'yeti', dens: 6, count: 2, loners: 6 },
        ],
        elites: { rare: 'ice-bear', epic: 'howling-yeti', legendary: 'elder-blizzard' },
        houses: ['cabin'],
        buildings: ['longhouse', 'forge-snow'],
        services: {
            forge: 'forge-snow',
            foundry: 'foundry-snow',
            alchemy: 'alchemy-snow',
            shop: 'shop-snow',
            auction: 'auction-snow',
        },
        questGiver: 'jarl',
        colour: '#e6eef5',
    },
];
export const BIOMES = Object.freeze(Object.fromEntries(DEFINITIONS.map((raw) => {
    const biome = BiomeDefinitionSchema.parse(raw);
    return [biome.id, biome];
})));
//# sourceMappingURL=biomes.js.map